import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    initialPlotDataOptimization,
    optimizeScreenName,
    Localization,
    Tab,
    FetchTypes,
    SliceTypes,
} from 'utils';
import { projectionsInitial } from './InitialStates/projectionsInitial';
import { RootState } from './rootReducer';
import { updateNotification } from './serviceSlice';
import {
    BASE_REACT_APP_CF_CORE_LEARNING_SCREENS,
    BASE_REACT_APP_CF_SCENARIO_PLAYING_AND_DRIVERS_OF_GROWTH,
} from '../App';

function getFileNameOnly(filePath: any) {
    return filePath.split('/').pop().split('.').shift();
}

export function filesGenerator(context: any) {
    const result: any = {};
    context.keys().forEach((key: any) => {
        const obj = context(key);
        const simpleKey = getFileNameOnly(key);
        result[simpleKey] = obj;
    });
    return result;
}

export const generatedPlotlyData = () => {
    const populationContext = require.context(`JSON/Population`, false, /\.json$/);
    const commercialOppContext = require.context(`JSON/Commercial_Opportunity`, false, /\.json$/);
    const demographicContext = require.context(`JSON/Demographic`, false, /\.json$/);
    const categorySpendContext = require.context(`JSON/Category_Spend`, false, /\.json$/);
    const currentSpendContext = require.context(`JSON/Current_Spend`, false, /\.json$/);
    const diversOfGrowthContext = require.context(`JSON/Drivers_of_Growth`, false, /\.json$/);

    return {
        [Tab.POPULATION]: initialPlotDataOptimization(filesGenerator(populationContext)),
        [Tab.DEMOGRAPHIC]: initialPlotDataOptimization(filesGenerator(demographicContext)),
        [Tab.DISPOSABLE_INCOME]: initialPlotDataOptimization(filesGenerator(commercialOppContext)),
        [Tab.CURRENT_SPEND]: initialPlotDataOptimization(filesGenerator(currentSpendContext)),
        [Tab.CATEGORY_SPEND]: initialPlotDataOptimization(filesGenerator(categorySpendContext)),
        [Tab.DRIVERS_OF_GROWTH]: initialPlotDataOptimization(filesGenerator(diversOfGrowthContext)),
    };
};

export const fetchScreenPlotsUS: any = createAsyncThunk(
    FetchTypes.SCREEN_PLOTS,
    async (params: any, thunkApi: any) => {
        const { tab } = params;

        try {
            const response = await axios.post(
                BASE_REACT_APP_CF_SCENARIO_PLAYING_AND_DRIVERS_OF_GROWTH,
                params,
            );
            return { data: response.data.screen_plots[tab], tab };
        } catch (err: any) {
            thunkApi.dispatch(
                updateNotification({
                    type: 'error',
                    header: 'Drivers of Growth Tab',
                    message: `Something went wrong!`,
                }),
            );
            console.error(err);
            throw err;
        }
    },
);

export const fetchHareData: any = createAsyncThunk(
    FetchTypes.HARE,
    async (params: any, thunkApi: any) => {
        const { localization, tab, updatedState, retrigger } = params;
        const {
            sidePanel: {
                [localization]: { tortoiseResponse },
            },
        } = thunkApi.getState();
        const animal = tab === Tab.DRIVERS_OF_GROWTH ? 'dog' : 'hare';
        const color =
            tab === Tab.DRIVERS_OF_GROWTH
                ? 'Driver'
                : updatedState.aggregatedBy === 'None'
                ? null
                : updatedState.aggregatedBy;
        const facet_col = updatedState.subPlot === 'None' ? null : updatedState.subPlot;
        const xaxis =
            tab === Tab.DRIVERS_OF_GROWTH &&
            (updatedState.xAxis === 'None' ? null : updatedState.xAxis);

        try {
            const { data } = await axios.post(BASE_REACT_APP_CF_CORE_LEARNING_SCREENS, {
                animal,
                ...tortoiseResponse,
                screen_name: optimizeScreenName(tab),
                selections: {
                    color,
                    facet_col,
                    xaxis,
                    year: 2023,
                },
                dev: false,
            });
            return {
                data,
                tab,
                retrigger,
                localization,
            };
        } catch (err: any) {
            console.error(err);
            thunkApi.dispatch(
                updateNotification({
                    type: 'error',
                    header: 'Hare CF',
                    message: `Something went wrong!`,
                }),
            );
            throw err;
        }
    },
);

export const projectionsSlice = createSlice({
    name: SliceTypes.PROJECTIONS,
    initialState: projectionsInitial,
    reducers: {
        updateProjectionTab: (
            state,
            {
                payload: { localization, tab },
            }: PayloadAction<{ localization: Localization; tab: string }>,
        ) => {
            state[localization].currentTab = {
                title: tab,
                type: state[localization].tabs[tab].type,
            };
        },
        updateTabDataStatus: (
            state,
            { payload: { localization } }: PayloadAction<{ localization: Localization }>,
        ) => {
            const allTabs = { ...state[localization].tabs };
            for (let key in allTabs) {
                allTabs[key] = { ...allTabs[key], tortoiseUpdated: false };
            }
            state[localization].tabs = allTabs;
        },
        updateGeoMapSelectionAllTabs: (
            state,
            {
                payload: { geoMapSelectionAllTabs, localization },
            }: PayloadAction<{
                geoMapSelectionAllTabs: any;
                localization: Localization;
            }>,
        ) => {
            state[localization].geoMapSelectionAllTabs = geoMapSelectionAllTabs;
        },
        updateProjectionTabSelection: (
            state,
            {
                payload: { property, value, localization, tab },
            }: PayloadAction<{
                property: string;
                value: string | any;
                localization: Localization;
                tab: string;
            }>,
        ) => {
            const allTabs = state[localization].tabs;

            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    selectionState: {
                        ...allTabs[tab].selectionState,
                        [property]: value,
                    },
                },
            };
        },
        updateProjectionTabSubcategorySelection: (
            state,
            {
                payload: { property, value, localization, tab },
            }: PayloadAction<{
                property: string;
                value: string | any;
                localization: Localization;
                tab: string;
            }>,
        ) => {
            const allTabs = state[localization].tabs;

            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    selectionState: {
                        ...allTabs[tab].selectionState,
                        dropDownAllSubcategories: {
                            ...allTabs[tab].selectionState.dropDownAllSubcategories,
                            [property]: value,
                        },
                    },
                },
            };
        },
        resetTabSidePanel: (
            state,
            {
                payload: { tab, localization },
            }: PayloadAction<{
                tab: string;
                localization: Localization;
            }>,
        ) => {
            const allTabs = state[localization].tabs;
            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    selectionState: {
                        ...allTabs[tab].selectionState,
                        ...allTabs[tab].defaultSidePanelState,
                    },
                },
            };
        },
    },

    extraReducers(builder) {
        builder.addCase(fetchHareData.pending, (state, { meta: { arg } }) => {
            const { tab, updatedState, localization } = arg;
            const allTabs = state[localization].tabs;

            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    isHareLoading: true,
                    selectionState: {
                        ...allTabs[tab].selectionState,
                        hareSelection: { ...updatedState },
                    },
                },
            };
        });
        builder.addCase(fetchHareData.fulfilled, (state, { payload }) => {
            const { data, tab, retrigger, localization } = payload;
            const allTabs = state[localization].tabs;

            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    data,
                    tortoiseUpdated: retrigger ?? true,
                    isHareLoading: false,
                },
            };
        });
        builder.addCase(fetchHareData.rejected, (state, { meta: { arg } }) => {
            const { tab, previousState, localization } = arg;
            const allTabs = state[localization].tabs;

            state[localization].tabs = {
                ...allTabs,
                [tab]: {
                    ...allTabs[tab],
                    isHareLoading: false,
                    selectionState: {
                        ...allTabs[tab].selectionState,
                        hareSelection: { ...previousState },
                    },
                },
            };
        });
    },
});

export const {
    updateProjectionTab,
    updateProjectionTabSelection,
    updateProjectionTabSubcategorySelection,
    updateGeoMapSelectionAllTabs,
    updateTabDataStatus,
    resetTabSidePanel,
} = projectionsSlice.actions;

export const projectionSlice = (state: RootState) => state.projections;

export default projectionsSlice.reducer;
